import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Workout from "../models/Workout";
import WorkoutSession from "../models/WorkoutSession";

const WorkoutsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [workoutStats, setWorkoutStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadWorkouts();
    }
  }, [user]);

  const loadWorkouts = async () => {
    try {
      const workoutList = await Workout.getAll(user.uid);
      setWorkouts(workoutList);

      // Load stats for each workout
      const stats = {};
      await Promise.all(
        workoutList.map(async (workout) => {
          stats[workout.id] = await WorkoutSession.getStats(user.uid, workout.id);
        })
      );
      setWorkoutStats(stats);
    } catch (err) {
      console.error("Error loading workouts:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatLastPerformed = (lastPerformedAt) => {
    if (!lastPerformedAt) return "Never";

    const date = lastPerformedAt.toDate ? lastPerformedAt.toDate() : new Date(lastPerformedAt);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getTrendIcon = (trend) => {
    if (trend === null) return null;
    if (trend > 5) return { icon: "\u2191", color: "text-green-500", label: `+${trend}%` };
    if (trend < -5) return { icon: "\u2193", color: "text-red-500", label: `${trend}%` };
    return { icon: "\u2192", color: "text-gray-500", label: "stable" };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading workouts...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
          >
            <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            My Workouts
          </h1>
        </div>
        <button
          onClick={() => navigate("/add-workout")}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New
        </button>
      </div>

      {workouts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-md">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No workouts yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first recurring workout to track your progress over time
          </p>
          <button
            onClick={() => navigate("/add-workout")}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Create Workout
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {workouts.map((workout) => {
            const stats = workoutStats[workout.id] || {};
            const trend = getTrendIcon(stats.trend);

            return (
              <div
                key={workout.id}
                onClick={() => navigate(`/workout/${workout.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-4">
                  {workout.imgThumb && (
                    <img
                      src={workout.imgThumb}
                      alt={workout.name}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {workout.name}
                    </h3>
                    {workout.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1 mb-2">
                        {workout.description}
                      </p>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Last:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">
                          {formatLastPerformed(workout.lastPerformedAt)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Best:</span>
                        <span className="ml-1 text-gray-900 dark:text-white font-medium">
                          {stats.personalBest !== null ? `${stats.personalBest} ${workout.measurementUnit}` : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Sessions:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">
                          {stats.totalSessions || 0}
                        </span>
                      </div>
                      {trend && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Trend:</span>
                          <span className={`ml-1 ${trend.color} font-medium`}>
                            {trend.icon} {trend.label}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkoutsPage;
